import React, { useEffect, useState, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../api';
import {
  GetAgentQuestionsConfirmationsCountResponse,
  AgentEvents,
  ConfirmationStatusChangedEvent,
} from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import './notification-count.scss';

interface NotificationCountProps {
  agentId?: string;
}

const NotificationCount: React.FC<NotificationCountProps> = ({ agentId }) => {
  const { getAccessTokenSilently } = useAuth0();
  const pusherChannel = usePusher();
  const [count, setCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchCount = useCallback(async () => {
    setLoading(true);
    setError(false);

    try {
      const token = await getAccessTokenSilently();

      if (agentId) {
        // Fetch count for specific agent
        const response =
          await api.get<GetAgentQuestionsConfirmationsCountResponse>(
            `/agent-dashboard/${agentId}/questions-confirmations-count`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            },
          );

        if (response.data.success) {
          setCount(response.data.notification_number);
        } else {
          setError(true);
        }
      } else {
        // Fetch total count for all agents
        const response = await api.get<{
          success: boolean;
          total_notifications: number;
        }>('/agent-dashboard/total-notifications', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          setCount(response.data.total_notifications);
        } else {
          setError(true);
        }
      }
    } catch (err) {
      console.error('Error fetching notification count:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [agentId, getAccessTokenSilently]);

  // Initial fetch
  useEffect(() => {
    fetchCount();
  }, [fetchCount]);

  // Listen for Pusher events that affect notification counts
  useEffect(() => {
    if (!pusherChannel) return;

    const handleNotificationEvent = (data: { agentId?: string }) => {
      // If we're showing a specific agent, only reload if event is for that agent
      // If we're showing total, reload for any event
      if (!agentId || data.agentId === agentId) {
        fetchCount();
      }
    };

    // Handler for confirmation status changes
    const handleConfirmationStatusChanged = (
      data: ConfirmationStatusChangedEvent,
    ) => {
      // Reload count when confirmation status changes (removes from pending count)
      if (!agentId || data.agentId === agentId) {
        fetchCount();
      }
    };

    // Bind to all events that affect notification counts
    pusherChannel.bind(AgentEvents.QUESTION_UPDATED, handleNotificationEvent);
    pusherChannel.bind(AgentEvents.QUESTION_ANSWERED, handleNotificationEvent);
    pusherChannel.bind(
      AgentEvents.CONFIRMATION_STATUS_CHANGED,
      handleConfirmationStatusChanged,
    );

    return () => {
      pusherChannel.unbind(
        AgentEvents.QUESTION_UPDATED,
        handleNotificationEvent,
      );
      pusherChannel.unbind(
        AgentEvents.QUESTION_ANSWERED,
        handleNotificationEvent,
      );
      pusherChannel.unbind(
        AgentEvents.CONFIRMATION_STATUS_CHANGED,
        handleConfirmationStatusChanged,
      );
    };
  }, [pusherChannel, agentId, fetchCount]);

  // Only show if loaded successfully and count > 0
  if (loading || error || count === null) {
    return null;
  }

  if (count === 0) {
    return <div className='notification-count empty'></div>;
  }

  return <div className='notification-count'>{count}</div>;
};

export default NotificationCount;
